import { useEffect, useState } from "react";
import { Edit2, Trash2, Copy } from "lucide-react";

interface MessageContextMenuProps {
  x: number;
  y: number;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
  isOwnMessage: boolean;
  isAdmin: boolean;
  messageContent: string;
}

export const MessageContextMenu = ({
  x,
  y,
  onEdit,
  onDelete,
  onClose,
  isOwnMessage,
  isAdmin,
  messageContent,
}: MessageContextMenuProps) => {
  const [position, setPosition] = useState({ x, y });

  useEffect(() => {
    // Adjust position if menu would go off-screen
    const menuWidth = 200;
    const menuHeight = 150;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let adjustedX = x;
    let adjustedY = y;

    if (x + menuWidth > viewportWidth) {
      adjustedX = viewportWidth - menuWidth - 10;
    }

    if (y + menuHeight > viewportHeight) {
      adjustedY = viewportHeight - menuHeight - 10;
    }

    setPosition({ x: adjustedX, y: adjustedY });
  }, [x, y]);

  useEffect(() => {
    const handleClickOutside = () => onClose();
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const handleCopy = () => {
    navigator.clipboard.writeText(messageContent);
    onClose();
  };

  return (
    <div
      className="fixed z-50 bg-background/95 backdrop-blur-xl border border-border/50 rounded-xl shadow-2xl py-2 min-w-[180px] animate-in fade-in zoom-in-95 duration-200"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {isOwnMessage && (
        <button
          onClick={() => {
            onEdit();
            onClose();
          }}
          className="w-full px-4 py-2.5 text-left hover:bg-primary/10 transition-colors flex items-center gap-3 text-sm font-medium text-foreground"
        >
          <Edit2 size={16} className="text-primary" />
          <span>Edit Message</span>
        </button>
      )}

      <button
        onClick={handleCopy}
        className="w-full px-4 py-2.5 text-left hover:bg-primary/10 transition-colors flex items-center gap-3 text-sm font-medium text-foreground"
      >
        <Copy size={16} className="text-primary" />
        <span>Copy Text</span>
      </button>

      {(isOwnMessage || isAdmin) && (
        <>
          <div className="h-px bg-border/50 my-1.5 mx-2" />
          <button
            onClick={() => {
              onDelete();
              onClose();
            }}
            className="w-full px-4 py-2.5 text-left hover:bg-destructive/10 transition-colors flex items-center gap-3 text-sm font-medium text-destructive"
          >
            <Trash2 size={16} />
            <span>Delete Message</span>
          </button>
        </>
      )}
    </div>
  );
};
